import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import StudentAssignment from '@/components/StudentAssignment';
import { toast } from 'sonner';
import { Search, Play, FileText, Link2, Download, Users, BookOpen, X, Filter } from 'lucide-react';
import axios from 'axios';
import { downloadBlob } from '@/lib/uploadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_COLORS = {
  'Lección':     'bg-blue-100 text-blue-700',
  'Tarea':       'bg-amber-100 text-amber-700',
  'Corrección':  'bg-green-100 text-green-700',
  'Evaluación':  'bg-red-100 text-red-700',
  'Refuerzo':    'bg-purple-100 text-purple-700',
};

function ResourceIcon({ resource }) {
  const ct = resource.file_content_type || '';
  if (resource.resource_type === 'link') return <Link2 className="w-5 h-5 text-blue-400" />;
  if (ct.startsWith('video')) return <Play className="w-5 h-5 text-blue-500" />;
  if (ct === 'application/pdf') return <FileText className="w-5 h-5 text-red-400" />;
  return <Download className="w-5 h-5 text-slate-400" />;
}

function AssignModal({ resource, students, onSave, onCancel, token }) {
  const [selected, setSelected] = useState(resource.assigned_to || []);
  const [scheduledFrom, setScheduledFrom] = useState(resource.scheduled_from ? resource.scheduled_from.slice(0, 16) : '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/assign/${resource.content_id}`,
        {
          student_ids: selected,
          scheduled_from: scheduledFrom ? new Date(scheduledFrom).toISOString() : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      let msg = selected.length === 0 ? 'Visible para todos los estudiantes' : `Asignado a ${selected.length} estudiante(s)`;
      if (scheduledFrom) msg += ` desde ${new Date(scheduledFrom).toLocaleDateString()}`;
      toast.success(msg);
      onSave();
    } catch { toast.error('Error al asignar'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="font-bold text-slate-800">Asignar Recurso</h2>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-xs text-slate-500">Recurso: <strong>{resource.original_name || resource.link_title || resource.title}</strong></p>
          <p className="text-xs text-slate-400">Sección: <strong>{resource.section_label}</strong></p>
          <StudentAssignment
            students={students}
            selectedStudents={selected}
            setSelectedStudents={setSelected}
            label="Asignar a Estudiantes"
          />
          {/* Programador */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <span>Programador</span>
              <span className="text-[10px] text-slate-400 font-normal">(opcional — disponible desde esta fecha)</span>
            </label>
            <input
              type="datetime-local"
              value={scheduledFrom}
              onChange={e => setScheduledFrom(e.target.value)}
              className="w-full text-sm border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300 text-slate-700"
              data-testid="scheduled-from-input"
            />
            {scheduledFrom && (
              <button onClick={() => setScheduledFrom('')} className="text-xs text-slate-400 hover:text-red-400">
                Quitar programación
              </button>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white" data-testid="confirm-assign-resource-btn">
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Guardar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LibraryTab({ classId, students = [] }) {
  const { user, token } = useAuth();
  const isTeacher = user?.role === 'teacher' || user?.role === 'admin';
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterSection, setFilterSection] = useState('all');
  const [assigningResource, setAssigningResource] = useState(null);

  const fetchResources = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/library/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(res.data);
    } catch { toast.error('Error al cargar la biblioteca'); }
    finally { setLoading(false); }
  }, [classId, token]);

  useEffect(() => { fetchResources(); }, [fetchResources]);

  const sections = ['all', ...new Set(resources.map(r => r.section_label))];

  const filtered = resources.filter(r => {
    const matchSearch = !search ||
      r.title?.toLowerCase().includes(search.toLowerCase()) ||
      r.original_name?.toLowerCase().includes(search.toLowerCase()) ||
      r.link_title?.toLowerCase().includes(search.toLowerCase());
    const matchSection = filterSection === 'all' || r.section_label === filterSection;
    return matchSearch && matchSection;
  });

  if (loading) return <div className="flex items-center justify-center py-20 text-slate-400">Cargando...</div>;

  return (
    <div className="space-y-4" data-testid="library-tab">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="library-search-input"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar recursos..."
            className="pl-9 rounded-xl"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filterSection}
            onChange={e => setFilterSection(e.target.value)}
            className="text-sm border rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
            data-testid="library-filter-select"
          >
            {sections.map(s => (
              <option key={s} value={s}>{s === 'all' ? 'Todas las secciones' : s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Count */}
      <p className="text-xs text-slate-400">{filtered.length} recurso(s) encontrado(s)</p>

      {/* Resource list */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{search || filterSection !== 'all' ? 'No se encontraron recursos con ese filtro' : 'Esta clase no tiene recursos aún'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((resource, idx) => {
            const name = resource.original_name || resource.link_title || resource.title || 'Recurso';
            const isAssigned = resource.assigned_to && resource.assigned_to.length > 0;
            return (
              <div
                key={resource.id || resource.filename || `lib-${idx}`}
                className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-sm transition-all"
                data-testid={`library-resource-${idx}`}
              >
                <div className="w-9 h-9 rounded-xl bg-slate-50 border flex items-center justify-center flex-shrink-0">
                  <ResourceIcon resource={resource} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-700 truncate">{name}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${SECTION_COLORS[resource.section_label] || 'bg-slate-100 text-slate-600'}`}>
                      {resource.section_label}
                    </Badge>
                    {isAssigned && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-600 border-amber-200">
                        <Users className="w-2.5 h-2.5 mr-0.5" />{resource.assigned_to.length}
                      </Badge>
                    )}
                  </div>
                  {resource.title && resource.original_name && (
                    <p className="text-[11px] text-slate-400 truncate">{resource.title}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {resource.resource_type === 'file' && (
                    <button
                      onClick={async () => {
                        try {
                          await downloadBlob(resource.filename, resource.original_name, token);
                        } catch (err) {
                          toast.error(err.message || 'Error al descargar');
                        }
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                      title="Descargar archivo"
                      data-testid={`library-open-${idx}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  {resource.resource_type === 'link' && (
                    <a
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 rounded-lg text-slate-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                      title="Abrir enlace"
                    >
                      <Link2 className="w-4 h-4" />
                    </a>
                  )}
                  {isTeacher && (
                    <button
                      onClick={() => setAssigningResource(resource)}
                      className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors"
                      data-testid={`library-assign-${idx}`}
                    >
                      <Users className="w-3.5 h-3.5" /> Asignar
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign modal */}
      {assigningResource && (
        <AssignModal
          resource={assigningResource}
          students={students}
          token={token}
          onSave={() => { setAssigningResource(null); fetchResources(); }}
          onCancel={() => setAssigningResource(null)}
        />
      )}
    </div>
  );
}
